import { personRef } from "@/content/author";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { getAllPosts } from "@/lib/posts.functions";
import podcastCover from "@/assets/podcast-cover.jpg.asset.json";
import { hreflangLinks } from "@/lib/seo";
import { pageTitle } from "@/lib/brand";
import { LinkRows, PageIntro, PageSection } from "@/components/site/page";

const CANONICAL = "https://akillisurdurulebilirlik.com/podcast";
const RSS_URL = "https://akillisurdurulebilirlik.com/api/public/podcast/rss.xml";

const postsQO = queryOptions({
  queryKey: ["posts", "tr"],
  queryFn: () => getAllPosts({ data: { lang: "tr" } }),
});

export const Route = createFileRoute("/podcast")({
  head: () => ({
    meta: [
      { title: pageTitle("Podcast", "tr") },
      {
        name: "description",
        content:
          "Yapay zeka, iklim ve insan üzerine yazıların seslendirilmiş versiyonları. Spotify, Apple Podcasts ve diğer uygulamalardan dinleyin.",
      },
      { property: "og:title", content: "Podcast — Akıllı Sürdürülebilirlik" },
      {
        property: "og:description",
        content:
          "Arda Öztaşkın'ın yazılarının sesli versiyonu. Spotify, Apple Podcasts ve diğer uygulamalarda.",
      },
      { property: "og:image", content: `https://akillisurdurulebilirlik.com${podcastCover.url}` },
      { property: "og:url", content: CANONICAL },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Akıllı Sürdürülebilirlik Podcast",
        href: RSS_URL,
      },
      ...hreflangLinks("/podcast"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "PodcastSeries",
          name: "Akıllı Sürdürülebilirlik Podcast",
          url: CANONICAL,
          description:
            "Yapay zeka, iklim sistemleri, insan ve gelecek üzerine yazıların seslendirilmiş versiyonları.",
          inLanguage: "tr-TR",
          image: podcastCover.url,
          webFeed: RSS_URL,
          author: personRef(),
        }),
      },
    ],
  }),

  loader: ({ context }) => context.queryClient.ensureQueryData(postsQO),
  component: PodcastPage,
  errorComponent: ({ error }) => {
    if (typeof console !== "undefined") console.error(error);
    return (
      <div className="mx-auto max-w-3xl p-8 text-clay">
        Yükleme sırasında bir sorun oluştu.
      </div>
    );
  },
  notFoundComponent: () => <div className="p-8">Bulunamadı.</div>,
});

function formatMin(seconds: number | null | undefined): string {
  if (!seconds) return "";
  const m = Math.round(seconds / 60);
  return `${m} dk`;
}

function PodcastPage() {
  const { data: posts } = useSuspenseQuery(postsQO);
  const episodes = posts.filter((p) => p.hasAudio);

  const spotifySearchUrl = `https://open.spotify.com/search/${encodeURIComponent("Akıllı Sürdürülebilirlik")}`;

  return (
    <>
      <PageIntro
        crumbs={[{ label: "Podcast" }]}
        kicker="Sesli Yayın · Türkçe"
        title={
          <>
            Akıllı Sürdürülebilirlik <em className="italic">Podcast</em>
          </>
        }
        lede="Yapay zeka, iklim sistemleri, insan ve gelecek üzerine yazıların seslendirilmiş versiyonları. Her yeni yazı, yapay zekâ seslendirmesiyle aynı gün podcast'e düşer."
        aside={
          <img
            src={podcastCover.url}
            alt="Akıllı Sürdürülebilirlik Podcast kapak görseli"
            width={280}
            height={280}
            loading="eager"
            decoding="async"
            className="mx-auto w-full max-w-[280px] shadow-2xl"
          />
        }
      >
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={spotifySearchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center gap-2 bg-ink px-5 text-xs font-bold uppercase tracking-[0.2em] text-paper transition-colors hover:bg-clay"
          >
            Spotify
          </a>
        </div>
      </PageIntro>

      <PageSection title={`Bölümler (${episodes.length})`}>
        {episodes.length === 0 ? (
          <p className="py-8 text-ink/60">Henüz bölüm yok.</p>
        ) : (
          <LinkRows
            withExcerpt
            items={episodes.map((ep) => ({
              key: ep.slug,
              to: "/yazilar/$slug" as const,
              params: { slug: ep.slug },
              title: ep.title,
              excerpt: ep.excerpt,
              meta: [ep.date, ep.audioDurationSeconds ? formatMin(ep.audioDurationSeconds) : null].filter(Boolean).join(" · "),
            }))}
          />
        )}
      </PageSection>
    </>
  );
}
